/**
 * Plant agents: the PCs running the Sahas Forge Agent (tools/ingest-agent-rs) that send this org's exports on their
 * own. Org admins only; on Settings, Agent keys, and on the import hub, above the older script agent's upload keys.
 *
 *   • The table: every identity of the org, connected PCs first. A connected one names its PC and when it was last
 *     heard from (an activation, a file sent, or a pass that checks in with nothing to send). Re-issue makes a new
 *     file for a new or rebuilt PC, and the old file stops working; revoke ends an identity for good.
 *   • Issuing makes an identity file (.sfid) that exists only in the answer: it is downloaded then, never again
 *     (the plan's decisions of 2026-10-08), so the notice holds the page until the file has been downloaded.
 *   • Agent 0.3.0 (migration 0218): each PC's agent version and its last update (updated, rolled back or failed, with
 *     why), its mode, and whether its settings are managed from Sahas Forge, by whom and when. The PC's name opens its
 *     details; "Settings" opens "Manage settings" (plant-agent-settings.tsx), not for a revoked identity.
 *   • For platform admins only, the agent releases and key updates (agent-releases.tsx).
 *   • Agent 0.3.2 (migration 0220): a live dot by "Last heard from": online (and how), paused at the PC, or offline.
 *   • For platform admins only, two tabs: "Plant PCs" (all of the above) and "Logs" (agent-logs.tsx): the bundles the
 *     agents send, their connection checks in plain words, the download, "Ask for logs now", and each PC's online
 *     history. Anyone else sees the page as before, with no tabs.
 *
 * Views are pure (rendered in tests without providers); PlantAgentsPanel holds the hooks.
 */
import { Fragment, useState } from "react";
import { ChevronRight, Download, FileKey, Monitor } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import { ConfirmDialog, DangerConfirmDialog } from "@/lib/ui/confirm-dialog";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";
import { ApiClientError } from "@/lib/api-client";
import {
  type IngestAgent,
  type IngestAgentIssued,
  useIngestAgentsQuery,
  useIssueIngestAgent,
  useReissueIngestAgent,
  useRevokeIngestAgent,
} from "@/lib/api/ingest-agents";
import {
  MODE_HINTS,
  MODE_WORDS,
  type UpdateTone,
  agentState,
  agoWords,
  fmtWhen,
  managedSettingLines,
  managedWords,
  modeWords,
  presenceWords,
  type PresenceTone,
  relayedWords,
  sortAgents,
  updateWords,
} from "@/lib/plant-agents";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/lib/ui/tabs";
import { useOrg } from "@/lib/org-context";
import { ManageSettingsSheet } from "./plant-agent-settings";
import { AgentReleasesPanel } from "./agent-releases";
import { AgentLogsPanel } from "./agent-logs";

const TH = "whitespace-nowrap px-3 py-2 text-left font-semibold text-on-surface-muted";
const SETUP_DAYS = [1, 3, 7, 14, 30] as const;
const COLUMNS = 7;
/** The row's actions, compact so seven columns fit the settings page. */
const ROW_ACTION = "h-7 px-2";
const TONE_TEXT: Record<UpdateTone, string> = { success: "text-success", warning: "text-warning", danger: "text-danger" };
const PRESENCE_DOT: Record<PresenceTone, string> = { online: "bg-success", paused: "bg-warning", offline: "bg-danger" };

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) {
    const body = err.body as { error?: string; detail?: string; message?: string } | null;
    return body?.detail || body?.message || body?.error || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

function fmtDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

// ── Views (pure) ───────────────────────────────────────────────────────────────

/**
 * A PC's details, under its row: the agent's version, its last update with why it failed, its mode, and the settings
 * managed for it. `onManage` is absent for a revoked identity, whose settings cannot be managed.
 */
export function AgentDetail({ agent: a, now, onManage }: { agent: IngestAgent; now: number; onManage?: () => void }) {
  const up = updateWords(a, now);
  const managed = managedWords(a, now);
  const mode = a.managed_settings?.mode;
  const lines = managedSettingLines(a.managed_settings ?? {});
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-surface p-3 text-xs">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
        <dt className="text-on-surface-muted">Agent</dt>
        <dd className="text-on-surface">{a.agent_version ? `version ${a.agent_version}` : "not reported yet"}</dd>
        <dt className="text-on-surface-muted">Last update</dt>
        <dd className="text-on-surface">
          {up ? (
            <>
              <span className={TONE_TEXT[up.tone]}>{up.text}</span>
              {up.at && <span className="text-on-surface-muted"> on {fmtWhen(up.at)}</span>}
              {up.error && <div className="mt-0.5 break-words text-on-surface-muted">Why: {up.error}</div>}
            </>
          ) : (
            "none reported"
          )}
        </dd>
        <dt className="text-on-surface-muted">Mode</dt>
        <dd className="text-on-surface">{mode ? `${MODE_WORDS[mode]}: ${MODE_HINTS[mode]}` : "The PC's own"}</dd>
        <dt className="text-on-surface-muted">Settings</dt>
        <dd className="text-on-surface">
          {managed.managed ? `Managed from Sahas Forge, ${managed.control}` : "The PC's own"}
          {a.managed_seq > 0 && a.managed_at && (
            <span className="text-on-surface-muted">
              {" "}
              · last changed{a.managed_by ? ` by ${a.managed_by}` : ""} on {fmtWhen(a.managed_at)} (change {a.managed_seq})
            </span>
          )}
          {lines.length > 0 && (
            <ul className="mt-1 flex flex-col gap-0.5" aria-label="Managed settings">
              {lines.map(([label, value]) => (
                <li key={label}>
                  <span className="text-on-surface-muted">{label}:</span> {value}
                </li>
              ))}
            </ul>
          )}
        </dd>
      </dl>
      {onManage && (
        <div>
          <Button size="sm" variant="outline" onClick={onManage}>
            Manage settings
          </Button>
        </div>
      )}
    </div>
  );
}

export function PlantAgentsView({
  agents,
  now,
  openId,
  onToggle,
  onManage,
  onReissue,
  onRevoke,
}: {
  agents: IngestAgent[];
  now: number;
  /** The PC whose details are open, or null. */
  openId: string | null;
  onToggle: (id: string) => void;
  onManage: (a: IngestAgent) => void;
  onReissue: (a: IngestAgent) => void;
  onRevoke: (a: IngestAgent) => void;
}) {
  if (agents.length === 0) {
    return (
      <p className="text-sm text-on-surface-muted">
        No plant PCs yet. Create an identity file above for each PC that should send its exports.
      </p>
    );
  }
  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[56rem] text-sm">
          <thead className="border-b border-border">
            <tr>
              <th className={TH}>Plant PC</th>
              <th className={TH}>State</th>
              <th className={TH}>Runs on</th>
              <th className={TH}>Mode</th>
              <th className={TH}>Settings</th>
              <th className={TH}>Last heard from</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {sortAgents(agents).map((a, i) => {
              const st = agentState(a);
              const revoked = a.status === "revoked";
              const open = openId === a.id;
              const detailId = `plant-agent-detail-${a.id}`;
              const up = updateWords(a, now);
              const managed = managedWords(a, now);
              // The stripe by index, so a row's details share its background.
              const stripe = i % 2 === 0 ? "bg-surface-elevated/30" : "";
              return (
                <Fragment key={a.id}>
                  <tr
                    className={cn(
                      stripe,
                      open ? "border-b-0" : "border-b border-border last:border-b-0",
                      revoked && "opacity-60",
                    )}
                  >
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="flex items-center gap-1 whitespace-nowrap rounded-sm text-left font-medium text-on-surface hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-expanded={open}
                        aria-controls={open ? detailId : undefined}
                        onClick={() => onToggle(a.id)}
                      >
                        <ChevronRight
                          aria-hidden
                          className={cn("size-3.5 shrink-0 text-on-surface-muted transition-transform", open && "rotate-90")}
                        />
                        {a.name}
                      </button>
                      <div className="whitespace-nowrap pl-4.5 font-mono text-xs text-on-surface-muted">{a.identity_id}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={st.tone} className="whitespace-nowrap">
                        {st.text}
                      </Badge>
                      {st.detail && <div className="mt-0.5 text-xs text-on-surface-muted">{st.detail}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-on-surface-muted">
                      {a.host_name ? (
                        <>
                          <span className="font-mono text-on-surface">{a.host_name}</span>
                          {a.agent_version && <span> · agent {a.agent_version}</span>}
                        </>
                      ) : revoked ? (
                        "—"
                      ) : (
                        "not set up yet"
                      )}
                      {up && (
                        <div
                          data-update={a.update_state}
                          className={cn("mt-0.5", TONE_TEXT[up.tone])}
                          title={[up.at ? fmtWhen(up.at) : "", up.error ?? ""].filter(Boolean).join(": ") || undefined}
                        >
                          <span className="whitespace-nowrap">{up.text}</span>
                          {up.ago && <span className="text-on-surface-muted"> · {up.ago}</span>}
                        </div>
                      )}
                    </td>
                    <td
                      className={cn(
                        "whitespace-nowrap px-3 py-2 text-xs",
                        a.managed_settings?.mode ? "text-on-surface" : "text-on-surface-muted",
                      )}
                    >
                      {modeWords(a)}
                    </td>
                    <td className="px-3 py-2 text-xs text-on-surface-muted">
                      {managed.managed ? (
                        <>
                          <Badge variant="info" className="whitespace-nowrap">
                            {managed.text}
                          </Badge>
                          <div className="mt-0.5">{managed.control}</div>
                          {managed.by && (
                            <div className="wrap-anywhere" title={a.managed_at ? fmtWhen(a.managed_at) : undefined}>
                              {managed.by}
                            </div>
                          )}
                        </>
                      ) : (
                        managed.text
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-on-surface-muted">
                      {(() => {
                        const p = revoked ? null : presenceWords(a);
                        return p ? (
                          <div className="flex items-center gap-1.5 whitespace-nowrap font-medium text-on-surface" title={p.title} data-presence={p.tone}>
                            <span aria-hidden className={cn("size-2 shrink-0 rounded-full", PRESENCE_DOT[p.tone])} />
                            {p.text}
                          </div>
                        ) : null;
                      })()}
                      {a.last_seen_at ? (
                        <span title={fmtWhen(a.last_seen_at)}>{agoWords(a.last_seen_at, now)}</span>
                      ) : (
                        "never"
                      )}
                      {(() => {
                        const r = relayedWords(a, now);
                        return r ? (
                          <div className="mt-0.5" title={r.title} data-relayed>
                            {r.text}
                          </div>
                        ) : null;
                      })()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {!revoked && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={ROW_ACTION}
                            aria-label={`Manage settings of ${a.name}`}
                            onClick={() => onManage(a)}
                          >
                            Settings
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={ROW_ACTION}
                            aria-label={`Re-issue ${a.name}`}
                            onClick={() => onReissue(a)}
                          >
                            Re-issue
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={ROW_ACTION}
                            aria-label={`Revoke ${a.name}`}
                            onClick={() => onRevoke(a)}
                          >
                            Revoke
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr id={detailId} className={cn(stripe, "border-b border-border last:border-b-0", revoked && "opacity-60")}>
                      <td colSpan={COLUMNS} className="px-4 pb-3 pt-0">
                        <AgentDetail agent={a} now={now} onManage={revoked ? undefined : () => onManage(a)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * A just-issued identity file: the only time it can be downloaded. "I have saved the file" waits for a download, so
 * the file is not lost by a stray click.
 */
export function IdentityFileNotice({
  issued,
  reissued,
  replacedHost,
  downloaded,
  onDownload,
  onDone,
}: {
  issued: IngestAgentIssued;
  reissued: boolean;
  /** The PC a re-issue disconnected, when it had one. */
  replacedHost?: string | null;
  downloaded: boolean;
  onDownload: () => void;
  onDone: () => void;
}) {
  return (
    <Card className="flex flex-col gap-3 border-warning/60 p-4">
      <div className="text-sm font-medium text-on-surface">
        {reissued ? "A new identity file" : "The identity file"} for {issued.agent.name} ({issued.agent.identity_id}).
        Download it now: this is the only time it can be downloaded.
      </div>
      <div>
        <Button size="sm" onClick={onDownload}>
          <Download className="mr-1.5 size-3.5" />
          Download {issued.file_name}
        </Button>
      </div>
      <div className="text-xs text-on-surface-muted">
        Copy the file to the plant PC (a USB stick works) and open it in the Sahas Forge Agent&apos;s setup. It sets up
        one PC, once, until {fmtDay(issued.agent.setup_expires_at)}: until then keep it as safe as a password. If it is
        lost, re-issue it here and the lost file stops working.
        {reissued &&
          (replacedHost
            ? ` ${replacedHost} stops sending until a PC is set up with this file.`
            : " The old file no longer works.")}
      </div>
      <Button size="sm" variant="outline" className="w-fit" disabled={!downloaded} onClick={onDone}>
        I have saved the file
      </Button>
    </Card>
  );
}

// ── Container ──────────────────────────────────────────────────────────────────

/** Saves the file byte for byte (its signature covers the bytes). */
function saveIdentityFile(issued: IngestAgentIssued) {
  const url = URL.createObjectURL(new Blob([issued.identity_file], { type: "application/octet-stream" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = issued.file_name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function PlantAgentsPanel() {
  const query = useIngestAgentsQuery(true);
  const issue = useIssueIngestAgent();
  const reissue = useReissueIngestAgent();
  const revoke = useRevokeIngestAgent();
  const toast = useToast();
  const [name, setName] = useState("");
  const [days, setDays] = useState(7);
  const [issued, setIssued] = useState<{ file: IngestAgentIssued; replaced?: IngestAgent } | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [pendingReissue, setPendingReissue] = useState<IngestAgent | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<IngestAgent | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [managing, setManaging] = useState<IngestAgent | null>(null);
  const { isPlatformAdmin } = useOrg();
  const agents = query.data?.items ?? [];

  const show = (file: IngestAgentIssued, replaced?: IngestAgent) => {
    setIssued({ file, replaced });
    setDownloaded(false);
  };

  const onIssue = async () => {
    try {
      show(await issue.mutateAsync({ name: name.trim(), setup_valid_days: days }));
      setName("");
    } catch (err) {
      toast.push("error", messageOf(err, "Couldn't create the identity file."));
    }
  };

  const confirmReissue = async () => {
    if (!pendingReissue) return;
    try {
      show(await reissue.mutateAsync(pendingReissue.id), pendingReissue);
    } catch (err) {
      toast.push("error", messageOf(err, "Couldn't re-issue the identity file."));
    } finally {
      setPendingReissue(null);
    }
  };

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      await revoke.mutateAsync(pendingRevoke.id);
      toast.push("ok", `${pendingRevoke.name} can no longer send files.`);
    } catch (err) {
      toast.push("error", messageOf(err, "Couldn't revoke the identity."));
    } finally {
      setPendingRevoke(null);
    }
  };

  // The identity files, the PCs, and (platform admins) the agent releases: the whole page for anyone but a
  // platform admin, its first tab for one.
  const pcs = (
    <>
      {issued ? (
        <IdentityFileNotice
          issued={issued.file}
          reissued={issued.replaced !== undefined}
          replacedHost={issued.replaced?.status === "active" ? issued.replaced.host_name : null}
          downloaded={downloaded}
          onDownload={() => {
            saveIdentityFile(issued.file);
            setDownloaded(true);
          }}
          onDone={() => setIssued(null)}
        />
      ) : (
        <Card className="flex flex-wrap items-end gap-2 p-4">
          <div className="flex min-w-48 flex-1 flex-col gap-1 text-xs text-on-surface-muted">
            <label htmlFor="plant-agent-name">The plant PC&apos;s name</label>
            <Input
              id="plant-agent-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Melt shop PC"
              maxLength={200}
            />
          </div>
          <div className="flex flex-col gap-1 text-xs text-on-surface-muted">
            <label htmlFor="plant-agent-days">Set it up within</label>
            <NativeSelect
              id="plant-agent-days"
              className="w-28"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {SETUP_DAYS.map((d) => (
                <option key={d} value={d}>
                  {d === 1 ? "1 day" : `${d} days`}
                </option>
              ))}
            </NativeSelect>
          </div>
          <Button size="sm" disabled={!name.trim() || issue.isPending} onClick={() => void onIssue()}>
            <FileKey className="mr-1.5 size-3.5" />
            Create identity file
          </Button>
        </Card>
      )}
      {query.isError ? (
        <p className="text-sm text-danger">{messageOf(query.error, "Couldn't load the plant agents.")}</p>
      ) : (
        query.isSuccess && (
          <PlantAgentsView
            agents={agents}
            now={query.dataUpdatedAt}
            openId={openId}
            onToggle={(id) => setOpenId((cur) => (cur === id ? null : id))}
            onManage={setManaging}
            onReissue={setPendingReissue}
            onRevoke={setPendingRevoke}
          />
        )
      )}
      <AgentReleasesPanel />
    </>
  );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Monitor className="size-4 text-on-surface-muted" />
        <h2 className="whitespace-nowrap text-sm font-semibold text-on-surface">Plant agents</h2>
        <span className="text-xs text-on-surface-muted">
          PCs running the Sahas Forge Agent send this org&apos;s exports on their own
        </span>
      </div>
      {isPlatformAdmin ? (
        <Tabs defaultValue="pcs">
          <TabsList aria-label="Plant agents">
            <TabsTrigger value="pcs">Plant PCs</TabsTrigger>
            <TabsTrigger value="logs">Logs</TabsTrigger>
          </TabsList>
          <TabsContent value="pcs" className="flex flex-col gap-3 pt-3">
            {pcs}
          </TabsContent>
          <TabsContent value="logs" className="pt-3">
            <AgentLogsPanel agents={agents} agentsState={query.isError ? "error" : query.isSuccess ? "ready" : "loading"} />
          </TabsContent>
        </Tabs>
      ) : (
        pcs
      )}
      <ManageSettingsSheet agent={managing} onClose={() => setManaging(null)} />
      <ConfirmDialog
        open={pendingReissue !== null}
        onClose={() => setPendingReissue(null)}
        onConfirm={confirmReissue}
        title="Re-issue this identity file?"
        description={
          pendingReissue
            ? pendingReissue.host_name
              ? `${pendingReissue.host_name} stops sending ${pendingReissue.name}'s files at once, until a PC is set up with the new file. For a replaced PC, or a lost file.`
              : `The current file for ${pendingReissue.name} stops working. For a lost or expired file.`
            : ""
        }
        confirmLabel="Re-issue"
        destructive
      />
      <DangerConfirmDialog
        open={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        onConfirm={confirmRevoke}
        title="Revoke this identity for good?"
        description={
          pendingRevoke
            ? `${pendingRevoke.name} stops sending at once, and this cannot be undone: to connect the PC again, create a new identity file. Files it already sent stay.`
            : ""
        }
        confirmPhrase={pendingRevoke?.identity_id ?? ""}
        confirmLabel="Revoke"
      />
    </section>
  );
}
